import React, { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface TechBarItem {
  name: string;
  value: number;
  max: number;
}

interface Props {
  items: TechBarItem[];
  maxItems?: number;
}

const TOTAL_DOTS = 5;

const getColor = (value: number, max: number) => {
  const pct = (value / max) * 100;
  if (pct >= 75) return '#0d6e5b';
  if (pct >= 50) return '#f59e0b';
  return '#ef4444';
};

// Render a single dot: full, half, or empty
const Dot = ({ state, color, hovered }: { state: 'full' | 'half' | 'empty'; color: string; hovered: boolean }) => {
  const base: React.CSSProperties = {
    width: 12,
    height: 12,
    borderRadius: '50%',
    flexShrink: 0,
    transition: 'all 0.2s ease',
    transform: hovered && state !== 'empty' ? 'scale(1.15)' : 'scale(1)',
  };

  if (state === 'full') {
    return <div style={{ ...base, background: color }} />;
  }
  if (state === 'half') {
    return (
      <div style={{ ...base, background: `linear-gradient(to right, ${color} 50%, #e2e8f0 50%)` }} />
    );
  }
  return <div style={{ ...base, background: '#e2e8f0' }} />;
};

const TechBarChart: React.FC<Props> = ({ items, maxItems = 5 }) => {
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(0);

  const totalPages = Math.ceil(items.length / maxItems);
  const currentItems = items.slice(currentPage * maxItems, (currentPage + 1) * maxItems);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        {currentItems.map((item, idx) => {
          const exactDots = (item.value / item.max) * TOTAL_DOTS;
          const fullDots = Math.floor(exactDots);
          const hasHalf = exactDots - fullDots >= 0.4;
          const dotColor = getColor(item.value, item.max);
          const isHovered = hoveredIdx === idx;

          return (
            <div
              key={idx}
              onMouseEnter={() => setHoveredIdx(idx)}
              onMouseLeave={() => setHoveredIdx(null)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '12px',
                padding: '8px 12px',
                borderRadius: '10px',
                background: isHovered ? '#f8fafc' : 'transparent',
                border: isHovered ? '1px solid #e2e8f0' : '1px solid transparent',
                transition: 'all 0.18s ease',
                cursor: 'default',
                minWidth: 0,
              }}
            >
              {/* Skill name */}
              <span style={{
                fontSize: '13px',
                fontWeight: 600,
                color: '#334155',
                flex: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}>
                {item.name}
              </span>

              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
                {/* Score badge — visible on hover */}
                <span style={{
                  fontSize: '11px',
                  fontWeight: 700,
                  color: dotColor,
                  background: `${dotColor}15`,
                  padding: '2px 7px',
                  borderRadius: '6px',
                  opacity: isHovered ? 1 : 0,
                  transition: 'opacity 0.15s ease',
                  whiteSpace: 'nowrap',
                }}>
                  {item.value}/{item.max}
                </span>

                {/* 5 dot stars with half support */}
                <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                  {Array.from({ length: TOTAL_DOTS }).map((_, i) => {
                    const state = i < fullDots ? 'full' : (i === fullDots && hasHalf ? 'half' : 'empty');
                    return <Dot key={i} state={state} color={dotColor} hovered={isHovered} />;
                  })}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '16px', marginTop: '12px' }}>
          <button
            onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
            disabled={currentPage === 0}
            style={{
              background: 'transparent', border: 'none', cursor: currentPage === 0 ? 'not-allowed' : 'pointer',
              color: currentPage === 0 ? '#cbd5e1' : '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'center',
              padding: '4px', borderRadius: '4px'
            }}
          >
            <ChevronLeft size={18} />
          </button>

          <div style={{ display: 'flex', gap: '6px' }}>
            {Array.from({ length: totalPages }).map((_, i) => (
              <div key={i} style={{
                width: 6, height: 6, borderRadius: '50%',
                background: i === currentPage ? '#0d6e5b' : '#e2e8f0',
                transition: 'all 0.2s'
              }} />
            ))}
          </div>

          <button
            onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))}
            disabled={currentPage === totalPages - 1}
            style={{
              background: 'transparent', border: 'none', cursor: currentPage === totalPages - 1 ? 'not-allowed' : 'pointer',
              color: currentPage === totalPages - 1 ? '#cbd5e1' : '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'center',
              padding: '4px', borderRadius: '4px'
            }}
          >
            <ChevronRight size={18} />
          </button>
        </div>
      )}
    </div>
  );
};

export default TechBarChart;
